import React, { useState, useEffect, useRef } from 'react';
import { ptzApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import PTZController from '../ptz/PTZController';
import PresetGrid from '../ptz/PresetGrid';
import PTZSettingsPanel from '../ptz/PTZSettingsPanel';
import PTZAudioPanel from '../ptz/PTZAudioPanel';
import OnScreenPTZ from '../ptz/OnScreenPTZ';
import AddCameraModal from './AddCameraModal';
import CameraViewers from './CameraViewers';
import ChatPanel from './ChatPanel';
import ControllerNotesOverlay from './ControllerNotesOverlay';
import ControllerNotesTab from './ControllerNotesTab';
import ScheduledOperator from './ScheduledOperator';
import { useWebRtcStream } from '../../hooks/useWebRtcStream';
import { useDriverFor } from '../../hooks/useCameraDrivers';

const TYPE_COLORS = {
  success: 'var(--accent-green)',
  error:   'var(--accent-red)',
  warning: 'var(--accent-orange)',
  info:    'var(--accent-cyan)',
};
const TYPE_BG = {
  success: 'rgba(0,255,136,0.1)',
  error:   'rgba(255,51,102,0.14)',
  warning: 'rgba(244,123,32,0.12)',
  info:    'rgba(0,212,255,0.1)',
};

// How long without a new frame before the stream is called stalled. Roughly 2.5 keyframe
// intervals on the 2s cameras, so an ordinary GOP boundary or a brief network hiccup does
// not trip it, while a genuinely frozen picture is reported within a few seconds.
const STALL_AFTER_MS = 5000;

// One classic mouse notch reports deltaY 100. Trackpad gestures arrive as many small deltas,
// so wheel input is accumulated against this and converted to whole notches.
const WHEEL_NOTCH_PX = 100;
// Ceiling on notches coalesced into a single zoom step, since the step is exponential in it.
const MAX_WHEEL_STEP = 6;

const SUB_TABS = [
  { key: 'presets',  label: 'Presets' },
  { key: 'settings', label: 'Settings' },
  { key: 'audio',    label: 'Audio Settings' },
  { key: 'notes',    label: 'Controller Notes' },
];

// Hint shown in a PTZ sub-tab when the camera isn't connected/streaming in standard mode.
function SubTabHint({ connected, action }) {
  return (
    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '28px 24px', lineHeight: 1.6, background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-dim)' }}>
      {connected ? 'Switch PTZ to Standard mode' : 'Connect to the camera'} to {action}.
    </div>
  );
}

// Compact toast overlay rendered inside the video container
function StreamToastOverlay() {
  const { toasts, removeToast } = useAppStore();
  if (toasts.length === 0) return null;
  return (
    <div style={{
      position: 'absolute', top: '10px', left: '10px',
      zIndex: 20, display: 'flex', flexDirection: 'column', gap: '6px',
      maxWidth: '240px', pointerEvents: 'none',
    }}>
      {toasts.map(t => {
        const color = TYPE_COLORS[t.type] || TYPE_COLORS.info;
        const bg = TYPE_BG[t.type] || TYPE_BG.info;
        return (
          <div
            key={t.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '7px 10px',
              background: 'rgba(8,12,18,0.88)',
              border: `1px solid ${color}`,
              borderLeft: `3px solid ${color}`,
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
              color, fontFamily: 'var(--font-display)',
              fontSize: '11px', fontWeight: 600,
              letterSpacing: '0.03em', lineHeight: 1.3,
              backgroundColor: bg,
              backdropFilter: 'blur(8px)',
              pointerEvents: 'auto',
            }}
          >
            <div style={{ flex: 1 }}>{t.message}</div>
            <button
              onClick={() => removeToast(t.id)}
              style={{ background: 'none', border: 'none', color, cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0, opacity: 0.7, flexShrink: 0 }}
            >×</button>
          </div>
        );
      })}
    </div>
  );
}

export default function CameraView({ camera, onRefresh }) {
  const driver = useDriverFor(camera);
  const { setSelectedCamera, addToast, token, user } = useAppStore();
  const [connecting, setConnecting] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(camera.deviceInfo || null);
  const [ptzCapable, setPtzCapable] = useState(false);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [streamReady, setStreamReady] = useState(false);
  const [streamProtocol, setStreamProtocol] = useState(null);
  const [activeTab, setActiveTab] = useState('live');
  const [showEdit, setShowEdit] = useState(false);
  const [ptzMode, setPtzMode] = useState(() => localStorage.getItem(`studio_ptz_mode_${camera.id}`) || 'standard');
  const [activePreset, setActivePreset] = useState({ id: null, name: null });
  const [subTab, setSubTab] = useState('presets'); // presets | settings | audio | notes
  const [notesRefresh, setNotesRefresh] = useState(0);
  const [clickMarker, setClickMarker] = useState(null);
  // Which player is driving the picture. Replaces a bare `typeof window.JSMpeg` boolean
  // that was evaluated inline in render and could never change — so when a player failed
  // at runtime there was nothing to fall back to and the dead surface stayed mounted.
  //   webrtc  — <video>, H.264 straight from go2rtc, no transcode
  //   mpegts  — <canvas>, JSMpeg decoding MPEG-1 in JS (the previous default)
  //   snapshot— <img>, polled stills; last resort
  const [playerMode, setPlayerMode] = useState(() =>
    (typeof window.RTCPeerConnection !== 'undefined') ? 'webrtc'
      : (typeof window.JSMpeg !== 'undefined') ? 'mpegts'
      : 'snapshot');
  const [videoEl, setVideoEl] = useState(null); // callback ref: re-runs the hook when mounted
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const playerRef = useRef(null);
  const markerTimer = useRef(null);
  const streamBoxRef = useRef(null);
  const wheelStopRef = useRef(null);
  const wheelDirRef = useRef(0); // active continuous-zoom direction, 0 = stopped

  // The stream box is 16/9 and takes whatever width the column gives it, so its height is
  // decided by the column. In standard mode the 400px PTZ sidebar holds that width down; in
  // on-screen mode the sidebar is gone, the column spans the pane, and 16/9 of that runs off
  // the bottom of the window. That matters more in on-screen mode than it would elsewhere,
  // because the controls are overlaid on the video — pushing the video down pushes the
  // joystick off screen with it.
  //
  // Capping the width rather than the height keeps the box a true 16/9. Capping the height
  // instead would leave the box full-width with the picture letterboxed inside it, i.e. black
  // bars either side.
  const contentRef = useRef(null);
  const streamWrapRef = useRef(null);
  const [maxStreamWidth, setMaxStreamWidth] = useState(null);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    function measure() {
      // Space below the video that must stay on screen: the active-preset / viewers row and
      // the gap above it. Measured from the container rather than the viewport so scrolling
      // does not change the answer.
      const RESERVE_BELOW = 88;
      const PADDING = 32; // the container's 16px top and bottom
      const available = container.clientHeight - PADDING - RESERVE_BELOW;
      // Below a floor the cap stops helping and just makes the picture unusably small; let it
      // overflow and scroll instead.
      setMaxStreamWidth(available > 180 ? Math.round((available * 16) / 9) : null);
    }

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [activeTab, ptzMode]);
  const zoomStepRef = useRef({ pending: 0, running: false }); // coalesced wheel notches (Dahua)
  const wheelAccumRef = useRef(0);                            // sub-notch wheel delta (Dahua)
  const zoomBaseRef = useRef(null);                           // { mm, pan, tilt, at } running burst position
  const zoomRangeRef = useRef(null);                          // { min, max } calibrated optical range


  // Click-to-center & wheel-zoom: any PTZ camera (Axis center / Dahua moveDirectly),
  // while the live stream is up.
  const canClickCenter = ptzCapable && connected && streamReady;

  async function handleVideoCenter(e) {
    if (!canClickCenter) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    // Native media size, to undo objectFit:contain letterboxing.
    // videoWidth MUST be tried first: a <video> also exposes a `width` attribute that is
    // 0 when unset, so checking naturalWidth/width first silently falls through to the
    // element box. mediaAspect would then equal elAspect, the letterbox correction would
    // collapse to zero, and every click would land off-target on any non-16:9 source.
    const nw = el.videoWidth || el.naturalWidth || el.width || rect.width;
    const nh = el.videoHeight || el.naturalHeight || el.height || rect.height;
    if (!nw || !nh) return;
    const elAspect = rect.width / rect.height;
    const mediaAspect = nw / nh;
    let contentW, contentH, offX, offY;
    if (mediaAspect > elAspect) {
      contentW = rect.width; contentH = rect.width / mediaAspect;
      offX = 0; offY = (rect.height - contentH) / 2;
    } else {
      contentH = rect.height; contentW = rect.height * mediaAspect;
      offY = 0; offX = (rect.width - contentW) / 2;
    }
    const localX = e.clientX - rect.left - offX;
    const localY = e.clientY - rect.top - offY;
    const x = localX / contentW;
    const y = localY / contentH;
    if (x < 0 || x > 1 || y < 0 || y > 1) return; // clicked in letterbox bars

    // Feedback marker, positioned relative to the video box (the media fills it)
    setClickMarker({ left: offX + localX, top: offY + localY, key: e.timeStamp });
    if (markerTimer.current) clearTimeout(markerTimer.current);
    markerTimer.current = setTimeout(() => setClickMarker(null), 650);

    setActivePreset({ id: null, name: null });
    const speed = parseInt(localStorage.getItem(`axisvms_ptz_center_speed_${camera.id}`) || '100');
    try {
      await ptzApi.ptzCenter(camera.id, { x, y, speed });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Center failed' });
    }
  }

  // Mouse-wheel zoom over the video. Native non-passive listener so preventDefault
  // actually suppresses page scroll (React binds wheel passively).
  //
  // Axis/Hikvision: each notch starts a continuous zoom and a debounced stop fires after
  // the last notch.
  //
  // Dahua: one absolute move per notch, no stop request at all. Continuous zoom is unsafe
  // here because start and stop are two independent requests whose delivery times vary
  // widely (measured 204-881ms against a live camera). The lens moves for
  // 300ms + (stop delay - start delay), so the step size tracks latency rather than the
  // notch, and when the stop happens to be delivered faster than the start it arrives
  // first — the camera is told to stop while idle, then to start, and nothing stops it
  // again until it hits the optical limit. An absolute move is a single request: latency
  // changes when it lands, never how far it goes. This mirrors what the zoom slider
  // already does for Dahua (PTZController.jsx startZoomToTarget).
  useEffect(() => {
    const el = streamBoxRef.current;
    if (!el) return;

    async function zoomRange() {
      if (zoomRangeRef.current) return zoomRangeRef.current;
      const { opticalZoomMin: min, opticalZoomMax: max } = camera;
      if (typeof min === 'number' && typeof max === 'number' && max > min) {
        zoomRangeRef.current = { min, max };
      } else {
        const r = await ptzApi.ptzLimits(camera.id);
        zoomRangeRef.current = { min: r.data.minZoom, max: r.data.maxZoom };
      }
      return zoomRangeRef.current;
    }

    // Position to step from. Reuses the burst's running value and only re-reads once the
    // burst goes quiet, because a position read costs a full round trip to the camera.
    // The 2s window also bounds how stale the pan/tilt we echo back can be.
    async function zoomBase() {
      const cached = zoomBaseRef.current;
      if (cached && Date.now() - cached.at < 2000) return cached;
      const r = await ptzApi.ptzPosition(camera.id);
      return { mm: r.data.zoom, pan: r.data.pan, tilt: r.data.tilt, at: Date.now() };
    }

    // Notches arriving while a move is in flight accumulate and apply as one step, so a
    // fast spin issues one command per round trip instead of one per notch while still
    // travelling the full notch count. Zoom is geometric (position is focal length), so a
    // notch feels the same at 1x and at 25x.
    function stepZoomDahua(dir) {
      const st = zoomStepRef.current;
      // Clamped because the step is exponential in `pending`: without a ceiling one hard
      // flick can still ask for a multiple that clamps at the travel limit, which reads as
      // the control overshooting rather than moving fast.
      st.pending = Math.max(-MAX_WHEEL_STEP, Math.min(MAX_WHEEL_STEP, st.pending + dir));
      if (st.running) return;
      st.running = true;
      (async () => {
        try {
          while (st.pending !== 0) {
            const steps = st.pending;
            st.pending = 0;
            const { min, max } = await zoomRange();
            const base = await zoomBase();
            const speed = parseInt(localStorage.getItem(`axisvms_ptz_mousezoom_speed_${camera.id}`) || '50');
            const k = 1 + (Math.max(1, Math.min(100, speed)) / 100) * 0.2; // 50 => 1.10 per notch
            const target = Math.max(min, Math.min(max, base.mm * Math.pow(k, steps)));
            if (Math.abs(target - base.mm) < 0.01) {      // already at the travel limit
              zoomBaseRef.current = { ...base, at: Date.now() };
              continue;
            }
            await ptzApi.ptzAbsolute(camera.id, {
              pan: base.pan, tilt: base.tilt, zoom: parseFloat(target.toFixed(3)),
            });
            zoomBaseRef.current = { mm: target, pan: base.pan, tilt: base.tilt, at: Date.now() };
          }
        } catch (_) {
          zoomBaseRef.current = null; // force a fresh read rather than compounding a bad base
          st.pending = 0;
        } finally {
          st.running = false;
        }
      })();
    }

    function onWheel(e) {
      if (!canClickCenter) return;
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1; // wheel up = zoom in (tele)
      setActivePreset({ id: null, name: null });

      // Absolute-track cameras step the wheel by position; continuous-track ones start a move
      // and stop it on a debounce. Which one this camera needs comes from its driver.
      if (driver?.zoomControl.track === 'absolute') {
        // Count scroll *distance*, not scroll events. A mouse fires one event per notch, but
        // a trackpad fires dozens of small ones per gesture and keeps firing through the
        // momentum, and the step here is exponential in the count — base * k^n. At k=1.10 a
        // forty-event flick asks for 1.10^40, about 45x, which slams into the travel limit.
        // That is why the smallest two-finger swipe zoomed the whole way in or out.
        //
        // Accumulating deltaY instead makes one physical notch and one trackpad gesture of the
        // same distance mean the same thing. deltaMode is normalised because Firefox reports
        // lines rather than pixels.
        const scale = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1;
        wheelAccumRef.current += e.deltaY * scale;
        const notches = Math.trunc(wheelAccumRef.current / WHEEL_NOTCH_PX);
        if (!notches) return;                    // gesture has not travelled a full notch yet
        wheelAccumRef.current -= notches * WHEEL_NOTCH_PX;
        stepZoomDahua(-notches);                 // wheel up is negative deltaY, and zooms in
        return;
      }

      // Axis/Hikvision keep the continuous zoom: one start, then a debounced stop. Only
      // send a start when the direction actually changes — the move already runs until the
      // stop, so one per notch just floods the camera. A single flick is a dozen requests,
      // which on a remote camera stack up past the request timeout and stall position
      // polling long after the zoom itself finished. (Dahua returned above: its wheel uses
      // absolute steps, because there start/stop can be delivered out of order.)
      const speed = parseInt(localStorage.getItem(`axisvms_ptz_mousezoom_speed_${camera.id}`) || '50');
      if (dir !== wheelDirRef.current) {
        wheelDirRef.current = dir;
        ptzApi.ptzMove(camera.id, { zoom: dir * speed, continuousMove: true, speed }).catch(() => {});
      }
      if (wheelStopRef.current) clearTimeout(wheelStopRef.current);
      wheelStopRef.current = setTimeout(() => {
        wheelDirRef.current = 0;
        ptzApi.ptzStop(camera.id).catch(() => {});
        wheelStopRef.current = null;
      }, 300);
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canClickCenter, camera.id, driver]);

  const streamUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/stream/${camera.id}?token=${token}`;
  const mjpegUrl = `/api/stream/${camera.id}/mjpeg?token=${token}`;

  function switchPtzMode(mode) {
    setPtzMode(mode);
    localStorage.setItem(`studio_ptz_mode_${camera.id}`, mode);
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const connectApi = ptzApi;
      const res = await connectApi.connect(camera.id);
      if (res.data.success) {
        setDeviceInfo(res.data.deviceInfo);
        setPtzCapable(res.data.ptzCapable);
        setConnected(true);
        addToast({ type: 'success', message: `Connected to ${camera.name}` });
        // startStream() is called by the useEffect below after React re-renders
        // the canvas into the DOM — calling it here would find videoRef.current === null
      }
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Connection failed' });
    } finally {
      setConnecting(false);
    }
  }

  // Liveness. `streamReady` cannot answer this: it goes true on the first decoded frame and
  // is only cleared on disconnect, pause, or a player switch — so once a stream had started,
  // it stayed "ready" whether frames were still arriving or the feed had frozen solid.
  //
  // That is the one thing an operator cannot check by looking. These cameras watch elk
  // fields and harbours; on a still scene, especially at night, a frozen frame and a running
  // one are identical. So this tracks actual frame arrival and says so.
  const lastFrameAtRef = useRef(0);
  const [framesFlowing, setFramesFlowing] = useState(false);

  // Recovery is immediate, staleness is noticed by the poller below. The guard matters:
  // requestVideoFrameCallback fires per presented frame, so 30-60 times a second, and an
  // unconditional setState there would re-render the whole view at frame rate. Only the
  // frame that ends a stall — including the very first one, when the ref is still 0 — writes
  // state; the rest just touch the ref.
  const markFrame = React.useCallback(() => {
    const now = Date.now();
    // 0 means "no frame this session" and must count as stalled in its own right. Leaving it
    // to the arithmetic would only work because Date.now() is large, and would break on
    // reconnect, where the ref still holds the previous session's timestamp: a reconnect
    // within the threshold would leave the badge reading Stalled until the poller caught up.
    const wasStalled = lastFrameAtRef.current === 0
      || now - lastFrameAtRef.current >= STALL_AFTER_MS;
    lastFrameAtRef.current = now;
    if (wasStalled) setFramesFlowing(true);
  }, []);

  // Absence of frames raises no event, so poll. Only commits a change, so a healthy stream
  // does not re-render once a second.
  useEffect(() => {
    // Clear the timestamp as well as the flag, so a new session never inherits the previous
    // one's frames and starts out claiming to be live.
    lastFrameAtRef.current = 0;
    if (!connected) { setFramesFlowing(false); return; }
    const id = setInterval(() => {
      const fresh = Date.now() - lastFrameAtRef.current < STALL_AFTER_MS;
      setFramesFlowing(prev => (prev === fresh ? prev : fresh));
    }, 1000);
    return () => clearInterval(id);
  }, [connected]);

  // WebRTC frame arrival. requestVideoFrameCallback fires per presented frame, which is
  // exactly the signal wanted; where it is missing, timeupdate fires while the media clock
  // advances and stops when it does not, which is the same question asked less precisely.
  useEffect(() => {
    if (!videoEl || playerMode !== 'webrtc' || !connected) return;
    if (typeof videoEl.requestVideoFrameCallback === 'function') {
      let handle, cancelled = false;
      const tick = () => {
        if (cancelled) return;
        markFrame();
        handle = videoEl.requestVideoFrameCallback(tick);
      };
      handle = videoEl.requestVideoFrameCallback(tick);
      return () => {
        cancelled = true;
        if (handle && videoEl.cancelVideoFrameCallback) videoEl.cancelVideoFrameCallback(handle);
      };
    }
    videoEl.addEventListener('timeupdate', markFrame);
    return () => videoEl.removeEventListener('timeupdate', markFrame);
  }, [videoEl, playerMode, connected, markFrame]);

  // WebRTC: H.264 straight from the camera via go2rtc, no transcode. Demotes to the
  // MPEG-TS path on any failure — ICE failure, no first frame, or the backend telling us
  // WebRTC isn't available for this camera (503) or can't carry its codec (422).
  useWebRtcStream({
    enabled: connected && playerMode === 'webrtc',
    cameraId: camera.id,
    videoEl,
    onReady: () => { setStreamReady(true); setStreamProtocol('WebRTC / H.264'); },
    onFailed: (reason) => {
      console.warn(`WebRTC unavailable (${reason}) — falling back to MPEG-TS`);
      setPlayerMode(typeof window.JSMpeg !== 'undefined' ? 'mpegts' : 'snapshot');
    },
  });

  // Start stream after connected=true causes the canvas to mount
  useEffect(() => {
    if (!connected) return;
    setStreamReady(false);
    if (playerMode !== 'mpegts') return;   // WebRTC drives itself; snapshot needs no player
    if (!window.JSMpeg || !videoRef.current) {
      setPlayerMode('snapshot');
      setStreamProtocol('Snapshot');
      setStreamReady(true);
      return;
    }
    try {
      if (playerRef.current) playerRef.current.destroy();
      playerRef.current = new window.JSMpeg.Player(streamUrl, {
        canvas: videoRef.current,
        autoplay: true, audio: false,
        videoBufferSize: 2 * 1024 * 1024,  // 2MB buffer — enough for late-join keyframe alignment
        onVideoDecode: () => { setStreamReady(true); markFrame(); },
      });
      setStreamProtocol('WebSocket / MPEG-TS');
    } catch (e) {
      // Actually demote now, rather than only relabelling — the old code left the dead
      // canvas mounted and the operator saw black with no indication why.
      console.error('JSMpeg failed:', e);
      setPlayerMode('snapshot');
      setStreamProtocol('Snapshot');
      setStreamReady(true);
    }
    // Tear the player (and its stream WebSocket) down when connected flips back to
    // false — i.e. on pause. Closing the WS lets the backend stop FFmpeg when no
    // clients remain. Nulling the ref keeps the unmount cleanup from double-destroying.
    return () => {
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (_) {}
        playerRef.current = null;
      }
    };
  // playerMode must be a dependency: a demotion has to tear the previous player down and
  // build the new one, which is exactly what the old [connected]-only list prevented.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, playerMode]);

  // Pause: stop the live preview without leaving the page. Play: resume it.
  function handlePause() {
    setPaused(true);
    setConnected(false);
    setStreamReady(false);
  }
  function handlePlay() {
    setPaused(false);
    setConnected(true);
  }

  // Volunteer operators don't get camera-management controls (Web UI / Edit).
  const isVolunteer = user?.role === 'volunteer_operator';
  // Who may reboot: admin/customer_admin always; volunteer operators only with the
  // per-user canReboot flag granted at setup. Mirrors the backend gate.
  const canReboot = user?.role === 'admin' || user?.role === 'customer_admin'
    || (isVolunteer && user?.canReboot === true);
  const [rebooting, setRebooting] = useState(false);
  const [showRebootConfirm, setShowRebootConfirm] = useState(false);

  async function doReboot() {
    setRebooting(true);
    try {
      await ptzApi.reboot(camera.id);
      addToast({ type: 'success', message: `Camera "${camera.name}" is rebooting…` });
      setShowRebootConfirm(false);
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Reboot failed' });
    } finally {
      setRebooting(false);
    }
  }

  useEffect(() => {
    return () => {
      if (playerRef.current) playerRef.current.destroy();
      if (wsRef.current) wsRef.current.close();
      if (markerTimer.current) clearTimeout(markerTimer.current);
      if (wheelStopRef.current) clearTimeout(wheelStopRef.current);
    };
  }, [camera.id]);

  const isOnline = camera.status?.online;

  // On-screen PTZ paints its controls over the frame: a ~48px column of tilt/zoom bars down
  // the right edge, a pan bar across the very bottom, and a wrapping preset row above that.
  // The chat and note overlays default to the two bottom corners, which is exactly where the
  // preset row and the pan bar are — and they sit at zIndex 25 against the PTZ layer's 5, so
  // they win and cover the controls.
  //
  // Reading up from the bottom edge the order is: pan bar, preset row, then the two
  // launchers side by side. The launchers move to clear the presets rather than the reverse,
  // because the preset row is the part whose height changes — it wraps to two lines or more
  // on a narrow frame or with long preset names. OnScreenPTZ measures it and reports it here.
  const onscreenPtz = ptzMode === 'onscreen' && ptzCapable && connected;
  const [ptzLayout, setPtzLayout] = useState({ bottomPresetsHeight: 0 });
  const onPtzLayout = React.useCallback(l => {
    setPtzLayout(prev => (prev.bottomPresetsHeight === l.bottomPresetsHeight ? prev : l));
  }, []);

  // 31px clears the 18px pan bar; +8 is the gap above the preset row. With no bottom row (the
  // presets are along another edge) the launchers drop back to just above the pan bar.
  const presetsH = onscreenPtz ? ptzLayout.bottomPresetsHeight : 0;
  const launcherBottom = onscreenPtz ? (presetsH ? 31 + presetsH + 8 : 37) : 16;
  const chatInset  = onscreenPtz ? { bottom: `${launcherBottom}px`, right: '58px' } : { bottom: '16px', right: '16px' };
  const notesInset = onscreenPtz ? { bottom: `${launcherBottom}px`, left: '16px' }  : { bottom: '16px', left: '16px' };

  // The Live badge sits top-right, which in on-screen mode is where the tilt and zoom bars
  // start: they run from top:8 down the right edge, tilt at right 30..48 and zoom at 6..24,
  // and they carry zIndex 10 while the badge carries none — so the bars and their T/Z labels
  // draw straight over it. Shift the badge inboard of the column, matching the chat launcher.
  const liveBadgeRight = onscreenPtz ? '58px' : '10px';

  return (
    <>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Back bar */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-dim)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: 'var(--bg-void)' }}>
          <button
            onClick={() => setSelectedCamera(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '6px 14px',
              background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.25)',
              borderRadius: 'var(--radius-md)', color: 'var(--accent-cyan)',
              cursor: 'pointer', fontFamily: 'var(--font-display)',
              fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.14)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.25)'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            All Cameras
          </button>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', overflow: 'hidden' }}>
            <span className={`status-dot ${isOnline ? 'online animate-pulse' : 'offline'}`} />
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '19px', letterSpacing: '0.05em', color: 'var(--accent-cyan)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '360px' }}>
              {camera.name}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
          {isOnline && (connected || paused) && (
            connected ? (
              <button className="btn btn-ghost btn-sm" onClick={handlePause} title="Pause preview stream">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                Pause
              </button>
            ) : (
              <button className="btn btn-ghost btn-sm" onClick={handlePlay} title="Resume preview stream">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg>
                Play
              </button>
            )
          )}
          {canReboot && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowRebootConfirm(true)} disabled={rebooting} title="Reboot this camera">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
              {rebooting ? 'Rebooting…' : 'Reboot Camera'}
            </button>
          )}
          {!isVolunteer && camera.ipAddress && (
            <a
              href={`http://${camera.ipAddress}:${camera.httpPort || 80}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost btn-sm"
              title="Open camera web interface"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
              Web UI
            </a>
          )}
          {!isVolunteer && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowEdit(true)} title="Edit Camera">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
          )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-dim)', padding: '0 16px', background: 'var(--bg-surface)', flexShrink: 0 }}>
          {['live', 'info'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '10px 16px', border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--accent-cyan)' : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab ? 'var(--accent-cyan)' : 'var(--text-secondary)',
              cursor: 'pointer', fontFamily: 'var(--font-display)',
              fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
              transition: 'all 0.15s',
            }}>
              {tab === 'live' ? `Live${ptzCapable ? ' + PTZ' : ''}` : 'Info'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {activeTab === 'live' && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>

              {/* Left column: stream + presets */}
              <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* Stream with toast overlay. maxWidth keeps 16/9 from running off the bottom
                    of the window — see maxStreamWidth above. Centred, so a capped video sits
                    in the middle of the pane rather than hugging the left edge. */}
                <div ref={streamWrapRef} style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: maxStreamWidth ? `${maxStreamWidth}px` : undefined,
                  margin: '0 auto',
                }}>
                  <div ref={streamBoxRef} style={{
                    background: 'var(--bg-void)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-dim)',
                    aspectRatio: '16/9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', position: 'relative',
                  }}>
                    {connected && isOnline ? (
                      <>
                        {playerMode === 'webrtc' ? (
                          <video
                            ref={setVideoEl}
                            autoPlay
                            muted
                            playsInline /* iOS Safari hijacks playback to fullscreen without this */
                            poster={`/api/snapshots/${camera.id}/live?token=${token}`}
                            onClick={handleVideoCenter}
                            title={canClickCenter ? 'Click to center camera here' : undefined}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', cursor: canClickCenter ? 'crosshair' : 'default' }}
                          />
                        ) : playerMode === 'mpegts' ? (
                          <canvas ref={videoRef} onClick={handleVideoCenter} title={canClickCenter ? 'Click to center camera here' : undefined} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', cursor: canClickCenter ? 'crosshair' : 'default' }} />
                        ) : (
                          <img src={mjpegUrl} alt="Live stream" onLoad={markFrame} onClick={handleVideoCenter} title={canClickCenter ? 'Click to center camera here' : undefined} style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: canClickCenter ? 'crosshair' : 'default' }} onError={e => { e.target.src = `/api/snapshots/${camera.id}/live?token=${token}`; }} />
                        )}
                        {clickMarker && (
                          <div key={clickMarker.key} className="ptz-center-ping" style={{ position: 'absolute', left: clickMarker.left, top: clickMarker.top, pointerEvents: 'none', zIndex: 15 }} />
                        )}
                        {connected && !streamReady && playerMode !== 'snapshot' && (
                          <div style={{
                            position: 'absolute', inset: 0,
                            background: '#000',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 10,
                          }}>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>
                              Connecting to stream...
                            </div>
                          </div>
                        )}
                        <div style={{ position: 'absolute', top: '10px', right: liveBadgeRight }}>
                          <span
                            className={`badge ${framesFlowing ? 'badge-online' : 'badge-unknown'}`}
                            title={framesFlowing
                              ? 'Frames are arriving from the camera'
                              : `No new frame in over ${STALL_AFTER_MS / 1000}s \u2014 the picture on screen may be frozen`}
                          >
                            <span className={`status-dot ${framesFlowing ? 'online animate-pulse' : 'unknown'}`} />
                            {framesFlowing ? 'Live' : 'Stalled'}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3" style={{ marginBottom: '16px' }}>
                          <circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/>
                          <line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/>
                          <line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/>
                        </svg>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
                          {!isOnline ? 'Camera Offline' : paused ? 'Stream Paused' : 'Stream Not Started'}
                        </div>
                        {isOnline && (
                          <button className="btn btn-primary" onClick={paused ? handlePlay : handleConnect} disabled={connecting}>
                            {connecting ? 'Connecting...' : paused ? 'Resume Stream' : 'Connect & Stream'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Toast overlay — top-left of video */}
                    <StreamToastOverlay />

                    {/* On-screen PTZ overlay */}
                    {ptzMode === 'onscreen' && ptzCapable && connected && (
                      <OnScreenPTZ camera={camera} onPresetActive={p => setActivePreset(p)} onLayout={onPtzLayout} />
                    )}

                    {/* Back-to-standard button in on-screen mode */}
                    {ptzMode === 'onscreen' && ptzCapable && connected && (
                      <button
                        onClick={() => switchPtzMode('standard')}
                        style={{
                          // Right-hand side, because that is where the standard PTZ panel
                          // appears. On the left it pointed away from the thing it summons.
                          // right:58 is the same inset the Live badge and chat launcher use
                          // to clear the tilt/zoom bar column; top:50 puts it under the badge.
                          position: 'absolute', top: '50px', right: '58px',
                          zIndex: 30,
                          display: 'flex', alignItems: 'center', gap: '5px',
                          padding: '4px 10px',
                          background: 'rgba(8,12,18,0.75)',
                          border: '1px solid rgba(0,212,255,0.35)',
                          borderRadius: '999px',
                          color: 'var(--accent-cyan)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-display)',
                          fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          backdropFilter: 'blur(6px)',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(8,12,18,0.75)'; }}
                      >
                        Standard ▶
                      </button>
                    )}
                  </div>

                  {/* Operator chat — anchored bottom-right of the stream */}
                  <ChatPanel cameraId={camera.id} inset={chatInset} />

                  {/* Controller Notes — floating add-note overlay, anchored near chat */}
                  <ControllerNotesOverlay cameraId={camera.id} inset={notesInset} onCreated={() => setNotesRefresh(x => x + 1)} />
                </div>

                {/* Controllers + active preset — directly below the preview video */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', minHeight: '34px' }}>
                  {activePreset.name ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '5px 14px', background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '999px' }}>
                      <span style={{ color: 'var(--accent-green)', fontSize: '9px', lineHeight: 1 }}>●</span>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: '14px', fontWeight: 700, letterSpacing: '0.04em', color: 'var(--accent-green)', whiteSpace: 'nowrap' }}>{activePreset.name}</span>
                    </div>
                  ) : <span />}
                  <CameraViewers cameraId={camera.id} avatarSize={30} fontSize={14} />
                </div>

                {/* Who's scheduled to control this camera (camera timezone) */}
                <ScheduledOperator camera={camera} />

                {/* Sub-tabs below the video: Presets · Settings · Audio · Controller Notes */}
                {(() => {
                  const ptzReady = ptzCapable && connected && ptzMode === 'standard';
                  return (
                    <div>
                      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border-dim)', marginBottom: '16px', overflowX: 'auto' }}>
                        {SUB_TABS.map(t => (
                          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
                            padding: '9px 14px', border: 'none',
                            borderBottom: subTab === t.key ? '2px solid var(--accent-cyan)' : '2px solid transparent',
                            background: 'transparent',
                            color: subTab === t.key ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                            cursor: 'pointer', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap',
                            fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                            transition: 'all 0.15s',
                          }}>
                            {t.label}
                          </button>
                        ))}
                      </div>

                      {subTab === 'presets' && (ptzReady
                        ? <PresetGrid camera={camera} activePresetId={activePreset.id} onPresetActive={p => setActivePreset(p)} />
                        : <SubTabHint connected={connected} action="manage presets" />)}
                      {subTab === 'settings' && (ptzReady
                        ? <PTZSettingsPanel camera={camera} />
                        : <SubTabHint connected={connected} action="adjust PTZ settings" />)}
                      {subTab === 'audio' && (ptzReady
                        ? <PTZAudioPanel camera={camera} />
                        : <SubTabHint connected={connected} action="adjust audio settings" />)}
                      {subTab === 'notes' && (
                        <ControllerNotesTab camera={camera} refreshKey={notesRefresh} />
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Right column: PTZ controls — hidden in on-screen mode */}
              {ptzCapable && connected && ptzMode === 'standard' && (
                <div style={{
                  flex: '0 0 400px',
                  overflowY: 'auto',
                  borderLeft: '1px solid var(--border-dim)',
                  paddingLeft: '16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                      PTZ Control
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {['standard', 'onscreen'].map(m => (
                        <button
                          key={m}
                          onClick={() => switchPtzMode(m)}
                          style={{
                            padding: '3px 8px',
                            border: '1px solid',
                            borderColor: ptzMode === m ? 'var(--accent-cyan)' : 'var(--border-dim)',
                            borderRadius: '999px',
                            background: ptzMode === m ? 'rgba(0,212,255,0.15)' : 'transparent',
                            color: ptzMode === m ? 'var(--accent-cyan)' : 'var(--text-muted)',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-display)',
                            fontSize: '9px', fontWeight: 700,
                            letterSpacing: '0.08em', textTransform: 'uppercase',
                            transition: 'all 0.15s',
                          }}
                        >
                          {m === 'standard' ? 'Standard' : 'On-Screen'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <PTZController camera={camera} onMove={() => setActivePreset({ id: null, name: null })} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'info' && (
            <InfoTab
              camera={camera}
              deviceInfo={deviceInfo}
              connected={connected}
              onConnect={handleConnect}
              connecting={connecting}
              streamProtocol={streamProtocol}
            />
          )}
        </div>
      </div>

      {showEdit && (
        <AddCameraModal
          editCamera={camera}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onRefresh(); }}
        />
      )}

      {showRebootConfirm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !rebooting && setShowRebootConfirm(false)}>
          <div className="modal" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Reboot Camera</h2>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => !rebooting && setShowRebootConfirm(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding: '20px', fontFamily: 'var(--font-display)', fontSize: '14px', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              Are you sure you want to reboot <strong style={{ color: 'var(--text-primary)' }}>{camera.name}</strong>? Rebooting the camera will take the live stream offline while the camera boots back up.
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setShowRebootConfirm(false)} disabled={rebooting}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={doReboot} disabled={rebooting}>
                {rebooting ? 'Rebooting…' : 'Reboot Camera'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InfoTab({ camera, deviceInfo, connected, onConnect, connecting, streamProtocol }) {
  const isOnline = camera.status?.online;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {!connected && isOnline && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <button className="btn btn-primary" onClick={onConnect} disabled={connecting}>
            {connecting ? 'Connecting...' : 'Connect to Camera'}
          </button>
          <div style={{ marginTop: '8px', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>
            Queries VAPIX API for device information
          </div>
        </div>
      )}

      {/* Stream info — shown when connected */}
      {connected && (
        <InfoSection title="Stream">
          {[
            // streamProtocol is set by whichever player actually took over, so it no
            // longer needs re-deriving here — and re-deriving it was already wrong once
            // the player could change at runtime.
            ['Protocol', streamProtocol || '—'],
            ['RTSP URL', `${camera.ipAddress}:${camera.rtspPort}`],
            ['Latency', camera.status?.latency ? `${camera.status.latency}ms` : '—'],
          ].map(([label, value]) => (
            <InfoRow key={label} label={label} value={value} highlight={label === 'Latency' && camera.status?.latency < 100} />
          ))}
        </InfoSection>
      )}

      {deviceInfo && (
        <InfoSection title="Device" accent="var(--accent-green)">
          {Object.entries({
            'Model': deviceInfo.model,
            'Product Number': deviceInfo.productNumber,
            'Firmware Version': deviceInfo.firmware,
            'Serial Number': deviceInfo.serialNumber,
            'Hardware ID': deviceInfo.hardwareId,
            'Connected At': deviceInfo.connectedAt ? new Date(deviceInfo.connectedAt).toLocaleString() : null,
          }).filter(([,v]) => v).map(([label, value]) => (
            <InfoRow key={label} label={label} value={value} />
          ))}
        </InfoSection>
      )}

      <InfoSection title="Configuration">
        {Object.entries({
          'IP Address': camera.ipAddress,
          'HTTP Port': camera.httpPort,
          'RTSP Port': camera.rtspPort,
          'Username': camera.username,
          'Location': camera.location || '—',
          'GPS': camera.gpsLat ? `${camera.gpsLat}, ${camera.gpsLng}` : '—',
          'Tags': camera.tags?.join(', ') || '—',
          'Added': new Date(camera.createdAt).toLocaleDateString(),
        }).map(([label, value]) => (
          <InfoRow key={label} label={label} value={value} />
        ))}
      </InfoSection>
    </div>
  );
}

function InfoSection({ title, accent, children }) {
  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-dim)', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-dim)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        {accent && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: accent, boxShadow: `0 0 6px ${accent}`, display: 'inline-block' }} />}
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: accent || 'var(--text-muted)' }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '8px 16px' }}>{children}</div>
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border-dim)', gap: '16px' }}>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: highlight ? 'var(--accent-green)' : 'var(--text-primary)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
